import { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import bbqLogo from '../assets/bbq-logo.png';
import PosterCarousel from '../PosterCarousel';
import Sidebar from '../components/Sidebar';
import About from '../components/About';
import Gallery from '../components/Gallery';
import Raffle from '../components/Raffle';
import Scoreboard from '../components/Scoreboard';
import Confetti from '../components/Confetti';
import './Home.css';

function Home() {
    const location = useLocation();
    const cookieName = Cookies.get('bbq_user_name');
    const name = location.state?.name || cookieName;
    const [members, setMembers] = useState([]);
    const [error, setError] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isLeaving, setIsLeaving] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    // Sidebar state
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('home');

    useEffect(() => {
        // Show confetti if user is newly joining (has name from location state)
        if (location.state?.name) {
            setShowConfetti(true);
            // Hide confetti after 6 seconds
            const confettiTimer = setTimeout(() => {
                setShowConfetti(false);
            }, 6000);
            return () => clearTimeout(confettiTimer);
        }
    }, [location.state]);

    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const response = await fetch('/api/members');
                const data = await response.json();
                setMembers(data.members);

                // If user is not in the member list anymore, remove their cookie
                // Only check after initial grace period
                if (!isInitialLoad && cookieName && !data.members.includes(cookieName)) {
                    Cookies.remove('bbq_user_name');
                }
            } catch (err) {
                console.error('Failed to fetch members:', err);
                setError('Failed to load party members');
            }
        };

        fetchMembers();
        // Set up polling to refresh the members list every 5 seconds
        const interval = setInterval(fetchMembers, 5000);

        // Set a timer to end the initial grace period after 2 seconds
        const graceTimer = setTimeout(() => {
            setIsInitialLoad(false);
        }, 2000);

        // Cleanup interval and timer on component unmount
        return () => {
            clearInterval(interval);
            clearTimeout(graceTimer);
        };
    }, [cookieName, isInitialLoad]);

    const handleLeaveParty = async () => {
        try {
            setIsLeaving(true);
            const response = await fetch('/api/leave', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: cookieName })
            });

            if (response.ok) {
                Cookies.remove('bbq_user_name');
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to leave the party');
                setIsLeaving(false);
            }
        } catch (err) {
            console.error('Failed to leave party:', err);
            setError('Failed to leave the party');
            setIsLeaving(false);
        }
    };

    const handleNavigate = (section) => {
        setActiveSection(section);
        setIsSidebarOpen(false);
    };

    // Redirect to login if user is not in the member list (after grace period)
    if (!isInitialLoad && !members.includes(cookieName)) {
        return <Navigate to="/" replace />;
    }

    const renderActiveSection = () => {
        switch (activeSection) {
            case 'about':
                return <About />;
            case 'gallery':
                return <Gallery />;
            default:
                return (
                    <>
                        <div className="home-challenges-section" style={{ marginBottom: '1rem', width: '100%' }}>
                            <Raffle />
                        </div>

                        <div className="home-challenges-section" style={{ marginBottom: '1rem', width: '100%' }}>
                            <Scoreboard />
                        </div>

                        <div className="party-members">
                            <h3>Current Party Members ({members.length})</h3>
                            {error ? (
                                <p className="error-message">{error}</p>
                            ) : (
                                <ul className="members-list">
                                    {members.map((memberName, index) => (
                                        <li key={index} className={memberName === name ? 'current-user' : ''}>
                                            {memberName} {memberName === name && '(You)'}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <button
                                className="leave-party-btn"
                                onClick={handleLeaveParty}
                                disabled={isLeaving}
                            >
                                {isLeaving ? 'Leaving...' : 'Leave Party'}
                            </button>
                        </div>
                    </>
                );
        }
    };

    return (
        <div className="app home-page">
            <Confetti active={showConfetti} duration={6000} />
            <button
                className="menu-button"
                onClick={() => setIsSidebarOpen(true)}
                aria-label="Open menu"
            >
                <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2" fill="none">
                    <path d="M3 12h18M3 6h18M3 18h18"></path>
                </svg>
            </button>

            <Sidebar
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                onNavigate={handleNavigate}
            />

            <div className="home-top-section">
                <img
                    src={bbqLogo}
                    alt="BBQ Logo"
                    className="logo top-logo spinning-logo"
                    onClick={() => handleNavigate('home')}
                />
                <h2 className="welcome-msg">Welcome, {name}! 🎉</h2>
            </div>

            <div className="home-content">
                {renderActiveSection()}
            </div>

            {activeSection !== 'gallery' && (
                <div className="carousel-section">
                    <div className="carousel-container bottom-carousel">
                        <PosterCarousel />
                    </div>
                </div>
            )}
        </div>
    );
}

export default Home; 